import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import {
  CheckCircle2,
  ArrowRight,
  Server,
  ShieldCheck,
  Loader2,
  Sparkles,
  Bot,
  BarChart3,
  FileText,
  Search,
  Building2,
  ChevronRight,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { authService, McpCallbackResponse } from '@/services/authService';
import ErrorModal from '@/components/common/ErrorModal';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { Button } from '@/common/components/button/Button';
import { Input } from '@/components/ui/Input';
import InisisAuthButton, { InisisAuthResult } from '@/components-common/inisis/InisisAuthButton';
import FactsheetLogo from '@/common/assets/logo/factsheet_logo.png';
import { LoginCompanyUserInfo, LoginCompany } from '@/common/types/login';

interface LoginForm {
  loginId: string;
  password: string;
}

type AuthTab = 'id' | 'phone';
type McpStep = 'login' | 'select-company' | 'result';

/**
 * MCP 서버 전용 인증 페이지
 * Step 1 - 로그인: 기존 ID/PW 또는 본인인증
 * Step 2 - 회사 선택: INVESTOR 스페이스 보유 회사가 2개 이상일 때
 * Step 3 - 결과: 연결 완료 화면
 */
const McpLogin: React.FC = () => {
  const [searchParams] = useSearchParams();
  const cb = searchParams.get('cb') ?? '';

  const [step, setStep] = useState<McpStep>('login');
  const [activeTab, setActiveTab] = useState<AuthTab>('id');
  const [form, setForm] = useState<LoginForm>({ loginId: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const [investorCompanies, setInvestorCompanies] = useState<LoginCompanyUserInfo[]>([]);
  const [isWindowClosed, setIsWindowClosed] = useState(false);

  useEffect(() => {
    if (!cb) {
      setError('유효하지 않은 접근입니다. 콜백 URL(cb)이 필요합니다.');
      setIsErrorModalOpen(true);
    }
  }, [cb]);

  /** 로그인 성공 후 INVESTOR 회사 필터링 및 다음 스텝 결정 */
  const handleAfterLogin = async () => {
    const profileResult = await authService.setUserProfile();
    const user = profileResult.data;
    const investors = (user?.companyUsers ?? []).filter(cu =>
      cu.company.services?.some(s => s.type === 'INVESTOR')
    );

    if (investors.length === 0) {
      setError('연결 가능한 투자사 스페이스가 없습니다.\n팩트시트에서 투자자 공간을 보유한 계정으로 로그인해주세요.');
      setIsErrorModalOpen(true);
      return;
    }

    if (investors.length === 1) {
      await callMcpCallback(investors[0].company);
    } else {
      setInvestorCompanies(investors);
      setStep('select-company');
    }
  };

  /** MCP callback API 호출 → 새창으로 redirectUrl 열고 현재 창 닫기 */
  const callMcpCallback = async (company: LoginCompany) => {
    setIsLoading(true);
    try {
      const res: McpCallbackResponse | null = await authService.mcpCallback({
        redirectUrl: cb,
        companyCode: company.code,
      });
      if (res?.success && res.redirectUrl) {
        window.open(res.redirectUrl, '_blank');
        setStep('result');
        setTimeout(() => {
          window.close();
          setIsWindowClosed(true);
        }, 300);
      } else {
        setError('MCP 연결에 실패했습니다.');
        setIsErrorModalOpen(true);
      }
    } catch (err: unknown) {
      setError((err as Error)?.message ?? 'MCP 연결 중 오류가 발생했습니다.');
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const validateIdForm = (): boolean => {
    if (!form.loginId.trim()) { setError('아이디를 입력해주세요.'); return false; }
    if (!form.password.trim()) { setError('비밀번호를 입력해주세요.'); return false; }
    return true;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleIdLogin = async () => {
    if (!cb) { setError('잘못된 접근입니다.'); setIsErrorModalOpen(true); return; }
    if (!validateIdForm()) { setIsErrorModalOpen(true); return; }

    setError('');
    setIsLoading(true);
    try {
      const res = await authService.login(form);
      if (res?.success) {
        await handleAfterLogin();
      } else {
        setError(res?.message ?? '로그인에 실패했습니다.');
        setIsErrorModalOpen(true);
      }
    } catch (err: unknown) {
      setError((err as Error)?.message ?? '로그인 중 오류가 발생했습니다.');
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCertify = async (certifyData: InisisAuthResult) => {
    if (!certifyData?.userDi || certifyData.userDi.length === 0) {
      setError('인증에 실패했습니다.'); setIsErrorModalOpen(true); return;
    }
    if (!cb) { setError('잘못된 접근입니다.'); setIsErrorModalOpen(true); return; }

    setIsLoading(true);
    try {
      const res = await authService.loginWithUserDi(certifyData.userDi, certifyData.userCi, certifyData.token);
      if (res?.success) {
        await handleAfterLogin();
      } else {
        setError(res?.message ?? '인증이 실패하였습니다.');
        setIsErrorModalOpen(true);
      }
    } catch (err: unknown) {
      setError((err as Error)?.message ?? '인증 중 오류가 발생했습니다.');
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const isLoginDisabled = isLoading || !cb;

  // ── Step 3: 결과 화면 (창 닫기 안내) ──
  if (step === 'result') {
    return (
      <PageWrapper>
        <CloseCard>
          <CloseIconWrapper>
            <CheckCircle2 size={36} />
          </CloseIconWrapper>
          <CloseHeading>인증이 완료되었습니다</CloseHeading>
          <CloseDescription>
            MCP 서버 연결이 성공적으로 완료되었습니다.<br />
            {isWindowClosed
              ? '이 창을 직접 닫아주세요.'
              : 'AI 도구 창으로 이동했습니다. 이 창은 닫아주세요.'}
          </CloseDescription>
          <CloseButton onClick={() => window.close()}>
            이 창 닫기
          </CloseButton>
          <CloseFooter>
            <ShieldCheck size={13} />
            <span>SSL 암호화로 안전하게 보호됩니다</span>
          </CloseFooter>
        </CloseCard>
      </PageWrapper>
    );
  }

  // ── Step 2: 회사 선택 화면 ──
  if (step === 'select-company') {
    return (
      <PageWrapper>
        <SelectCard>
          <SelectHeader>
            <SelectIconRing>
              <Building2 size={28} />
            </SelectIconRing>
            <SelectHeadingGroup>
              <SelectHeading>투자사 선택</SelectHeading>
              <SelectSubHeading>MCP 서버에 연결할 투자사 계정을 선택해주세요</SelectSubHeading>
            </SelectHeadingGroup>
          </SelectHeader>

          <SelectStepBadge>
            <StepDot $done />
            <StepLine $done />
            <StepDot $done />
            <StepLine />
            <StepDot />
          </SelectStepBadge>

          <CompanyList>
            {investorCompanies.map((cu) => (
              <CompanyItem
                key={cu.company.code}
                onClick={() => !isLoading && callMcpCallback(cu.company)}
                $disabled={isLoading}
              >
                <CompanyItemLogo>
                  {cu.company.logo
                    ? <CompanyLogoImg src={cu.company.logo} alt={cu.company.name} />
                    : <Building2 size={20} />
                  }
                </CompanyItemLogo>
                <CompanyItemInfo>
                  <CompanyItemName>{cu.company.name}</CompanyItemName>
                  <CompanyItemCode>{cu.company.code}</CompanyItemCode>
                </CompanyItemInfo>
                {isLoading
                  ? <Loader2 size={18} className="spin-icon" />
                  : <ChevronRight size={18} className="arrow-icon" />
                }
              </CompanyItem>
            ))}
          </CompanyList>

          <SelectFooter>
            <ShieldCheck size={13} />
            <span>SSL 암호화로 안전하게 보호됩니다</span>
          </SelectFooter>
        </SelectCard>
      </PageWrapper>
    );
  }

  // ── Step 1: 로그인 화면 ──
  return (
    <PageWrapper>
      <TwoColumnLayout>

        {/* 왼쪽: 로그인 폼 */}
        <Card>
          <ConnectionBanner>
            <AppChip>
              <AppLogo src={FactsheetLogo} alt="Factsheet" />
            </AppChip>
            <ConnectionArrow>
              <ArrowRight size={16} />
            </ConnectionArrow>
            <AppChip>
              <Server size={16} />
              <span>MCP Server</span>
            </AppChip>
          </ConnectionBanner>

          <Header>
            <CardTitle>계정 인증</CardTitle>
            <CardDescription>
              팩트시트 계정으로 로그인하여 MCP 서버 접근 권한을 부여합니다.
            </CardDescription>
          </Header>

          <TabGroup>
            <TabButton $active={activeTab === 'id'} onClick={() => setActiveTab('id')}>
              ID / 비밀번호
            </TabButton>
            <TabButton $active={activeTab === 'phone'} onClick={() => setActiveTab('phone')}>
              본인인증
            </TabButton>
            <TabIndicator $activeIndex={activeTab === 'id' ? 0 : 1} />
          </TabGroup>

          {activeTab === 'id' && (
            <FormSection>
              <FieldGroup>
                <FieldLabel htmlFor="loginId">아이디</FieldLabel>
                <Input
                  id="loginId"
                  type="text"
                  placeholder="아이디를 입력해 주세요."
                  name="loginId"
                  value={form.loginId}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && form.loginId.trim() && form.password.trim()) handleIdLogin();
                  }}
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel htmlFor="password">비밀번호</FieldLabel>
                <PasswordWrapper>
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="비밀번호를 입력해 주세요."
                    name="password"
                    value={form.password}
                    onChange={handleInputChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && form.loginId.trim() && form.password.trim()) handleIdLogin();
                    }}
                  />
                  <EyeButton type="button" onClick={() => setShowPassword(p => !p)}>
                    {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </EyeButton>
                </PasswordWrapper>
              </FieldGroup>
              <SubmitButton
                $variant="gradient"
                $size="md"
                $isFull
                disabled={isLoginDisabled}
                onClick={(e) => { e.preventDefault(); handleIdLogin(); }}
              >
                {isLoading ? <><Loader2 size={16} className="spin" /> 로그인 중...</> : '로그인'}
              </SubmitButton>
            </FormSection>
          )}

          {activeTab === 'phone' && (
            <PhoneSection>
              <PhoneGuide>
                <PhoneGuideIcon><ShieldCheck size={20} /></PhoneGuideIcon>
                <PhoneGuideText>
                  아이디나 비밀번호가 없어도<br />
                  <strong>본인인증</strong>으로 바로 로그인할 수 있습니다.
                </PhoneGuideText>
              </PhoneGuide>
              <InisisAuthButton
                $variant="gradient"
                $size="md"
                $title="본인인증으로 로그인"
                onSuccess={(data: InisisAuthResult) => handleCertify(data)}
              />
            </PhoneSection>
          )}

          <Footer>
            <ShieldCheck size={13} />
            <FooterText>SSL 암호화로 안전하게 보호됩니다</FooterText>
          </Footer>
        </Card>

        {/* 오른쪽: 가치 제안 패널 */}
        <ValuePanel>
          <ValuePanelInner>
            <ValueLogo src={FactsheetLogo} alt="Factsheet" />

            <ValueHeadingGroup>
              <ValueEyebrow>
                <Sparkles size={13} />
                AI × 팩트시트
              </ValueEyebrow>
              <ValueHeading>
                AI에게 그냥<br />물어보세요
              </ValueHeading>
              <ValueSubHeading>
                데이터를 복사해서 붙여넣지 않아도,<br />
                AI가 팩트시트 데이터를 직접 읽어 분석합니다.
              </ValueSubHeading>
            </ValueHeadingGroup>

            <FeatureList>
              <FeatureItem>
                <FeatureIconBox $color="#6366f1"><BarChart3 size={16} /></FeatureIconBox>
                <FeatureContent>
                  <FeatureTitle>포트폴리오 즉시 분석</FeatureTitle>
                  <FeatureDesc>
                    "이번 분기 투자 현황 요약해줘"처럼 말하면 AI가 실제 데이터 기반으로 즉시 답변합니다.
                  </FeatureDesc>
                </FeatureContent>
              </FeatureItem>
              <FeatureItem>
                <FeatureIconBox $color="#10b981"><FileText size={16} /></FeatureIconBox>
                <FeatureContent>
                  <FeatureTitle>리포트 자동 작성</FeatureTitle>
                  <FeatureDesc>
                    AI가 최신 팩트시트 데이터를 실시간으로 참조해 투자 리포트를 자동으로 작성합니다.
                  </FeatureDesc>
                </FeatureContent>
              </FeatureItem>
              <FeatureItem>
                <FeatureIconBox $color="#f59e0b"><Search size={16} /></FeatureIconBox>
                <FeatureContent>
                  <FeatureTitle>기업 정보 빠른 조회</FeatureTitle>
                  <FeatureDesc>
                    복잡한 검색 없이 AI에게 질문 하나로 기업 데이터 전체를 탐색합니다.
                  </FeatureDesc>
                </FeatureContent>
              </FeatureItem>
            </FeatureList>

            <CompatibleTools>
              <CompatibleLabel>지원 AI 도구</CompatibleLabel>
              <CompatibleChips>
                <ToolChip><Bot size={12} />Claude</ToolChip>
                <ToolChip><Bot size={12} />Cursor</ToolChip>
                <ToolChip><Bot size={12} />기타 MCP 도구</ToolChip>
              </CompatibleChips>
            </CompatibleTools>
          </ValuePanelInner>
        </ValuePanel>

      </TwoColumnLayout>

      <ErrorModal
        isOpen={isErrorModalOpen}
        message={error}
        onClose={() => setIsErrorModalOpen(false)}
      />
    </PageWrapper>
  );
};

// ─── Animations ──────────────────────────────────────────────────────────────

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.5); }
  to   { opacity: 1; transform: scale(1); }
`;

const spinAnim = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.primary};
  padding: 32px 16px;
`;

const TwoColumnLayout = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  max-width: 900px;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid ${semanticColors.border.primary};
  animation: ${fadeInUp} 0.5s cubic-bezier(0.16, 1, 0.3, 1);

  @media (max-width: 768px) {
    flex-direction: column;
    max-width: 480px;
  }
`;

// ─── Login Card ───────────────────────────────────────────────────────────────

const Card = styled.div`
  width: 420px;
  flex-shrink: 0;
  background: ${semanticColors.background.secondary};
  padding: 36px 36px 28px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 768px) {
    width: 100%;
    padding: 28px 24px 24px;
    gap: 20px;
  }
`;

const ConnectionBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AppChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: ${semanticColors.background.tertiary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 100px;
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.secondary};
`;

const AppLogo = styled.img`
  height: 16px;
  width: auto;
  object-fit: contain;
`;

const ConnectionArrow = styled.div`
  display: flex;
  align-items: center;
  color: ${semanticColors.text.disabled};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardTitle = styled.h1`
  ${typography.heading.h3}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const CardDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
  line-height: 1.6;
`;

const TabGroup = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: ${semanticColors.background.tertiary};
  border-radius: 12px;
  padding: 4px;
`;

const TabButton = styled.button<{ $active: boolean }>`
  position: relative;
  z-index: 1;
  padding: 10px 0;
  border: none;
  background: transparent;
  border-radius: 9px;
  ${typography.caption.caption1.medium}
  color: ${({ $active }) => $active ? semanticColors.text.primary : semanticColors.text.tertiary};
  cursor: pointer;
  transition: color 0.2s ease;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
`;

const TabIndicator = styled.div<{ $activeIndex: number }>`
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: ${({ $activeIndex }) => ($activeIndex === 0 ? '4px' : 'calc(50%)')};
  width: calc(50% - 4px);
  background: white;
  border-radius: 9px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  transition: left 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.secondary};
`;

const PasswordWrapper = styled.div`
  position: relative;
`;

const EyeButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${semanticColors.text.tertiary};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  &:hover { color: ${semanticColors.text.secondary}; }
`;

const SubmitButton = styled(Button)`
  margin-top: 4px;
  .spin { animation: ${spinAnim} 0.8s linear infinite; }
`;

const PhoneSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PhoneGuide = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(99,102,241,0.07) 0%, rgba(139,92,246,0.1) 100%);
  border: 1px solid rgba(99,102,241,0.18);
  border-radius: 14px;
`;

const PhoneGuideIcon = styled.div`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border-radius: 10px;
  color: white;
`;

const PhoneGuideText = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.secondary};
  line-height: 1.6;
  margin: 0;
  padding-top: 2px;
  strong { color: #6366f1; font-weight: 600; }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding-top: 4px;
  border-top: 1px solid ${semanticColors.border.primary};
  color: ${semanticColors.text.disabled};
`;

const FooterText = styled.span`
  ${typography.caption.caption2}
  color: ${semanticColors.text.disabled};
`;

// ─── Value Panel ──────────────────────────────────────────────────────────────

const ValuePanel = styled.div`
  flex: 1;
  background: linear-gradient(160deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -60px; right: -60px;
    width: 240px; height: 240px;
    border-radius: 50%;
    background: rgba(139,92,246,0.2);
    filter: blur(40px);
  }
  &::after {
    content: '';
    position: absolute;
    bottom: -40px; left: -40px;
    width: 200px; height: 200px;
    border-radius: 50%;
    background: rgba(99,102,241,0.25);
    filter: blur(50px);
  }

  @media (max-width: 768px) { display: none; }
`;

const ValuePanelInner = styled.div`
  position: relative;
  z-index: 1;
  padding: 40px 36px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  height: 100%;
`;

const ValueLogo = styled.img`
  height: 28px;
  width: auto;
  max-width: 180px;
  object-fit: contain;
  align-self: flex-start;
  filter: brightness(0) invert(1);
  opacity: 0.9;
`;

const ValueHeadingGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ValueEyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  ${typography.caption.caption1.medium}
  color: rgba(196,181,253,0.9);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  svg { flex-shrink: 0; }
`;

const ValueHeading = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.2;
  color: white;
  margin: 0;
  letter-spacing: -0.02em;
`;

const ValueSubHeading = styled.p`
  ${typography.paragraph.p2.default}
  color: rgba(196,181,253,0.85);
  margin: 0;
  line-height: 1.7;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const FeatureIconBox = styled.div<{ $color: string }>`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: ${({ $color }) => $color}28;
  border: 1px solid ${({ $color }) => $color}40;
  color: ${({ $color }) => $color};
  margin-top: 1px;
`;

const FeatureContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FeatureTitle = styled.span`
  ${typography.paragraph.p2.default}
  font-weight: 600;
  color: rgba(255,255,255,0.95);
`;

const FeatureDesc = styled.span`
  ${typography.caption.caption1.default}
  color: rgba(196,181,253,0.75);
  line-height: 1.5;
`;

const CompatibleTools = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid rgba(255,255,255,0.1);
`;

const CompatibleLabel = styled.span`
  ${typography.caption.caption2}
  color: rgba(196,181,253,0.6);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const CompatibleChips = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ToolChip = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 100px;
  ${typography.caption.caption2}
  color: rgba(255,255,255,0.7);
  svg { flex-shrink: 0; }
`;

// ─── Select Company Card ──────────────────────────────────────────────────────

const SelectCard = styled.div`
  width: 100%;
  max-width: 520px;
  background: ${semanticColors.background.secondary};
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(0,0,0,0.1);
  border: 1px solid ${semanticColors.border.primary};
  animation: ${fadeInUp} 0.45s cubic-bezier(0.16,1,0.3,1);
`;

const SelectHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 28px 32px;
  border-bottom: 1px solid ${semanticColors.border.primary};
`;

const SelectIconRing = styled.div`
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #312e81, #4c1d95);
  border-radius: 16px;
  color: white;
  animation: ${scaleIn} 0.4s cubic-bezier(0.34,1.56,0.64,1);
`;

const SelectHeadingGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SelectHeading = styled.h2`
  ${typography.heading.h3}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const SelectSubHeading = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
`;

const SelectStepBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 16px 32px 0;
`;

const StepDot = styled.div<{ $done?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $done }) => $done ? '#6366f1' : semanticColors.border.secondary};
  flex-shrink: 0;
`;

const StepLine = styled.div<{ $done?: boolean }>`
  flex: 1;
  height: 2px;
  max-width: 60px;
  background: ${({ $done }) => $done ? '#6366f1' : semanticColors.border.primary};
`;

const CompanyList = styled.div`
  padding: 20px 32px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CompanyItem = styled.div<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: ${semanticColors.background.tertiary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 14px;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};
  transition: all 0.2s ease;

  &:hover:not([data-disabled]) {
    border-color: #6366f1;
    background: rgba(99,102,241,0.04);
    box-shadow: 0 2px 12px rgba(99,102,241,0.1);
  }

  .arrow-icon {
    margin-left: auto;
    color: ${semanticColors.text.disabled};
    transition: transform 0.2s ease;
  }
  .spin-icon {
    margin-left: auto;
    color: #6366f1;
    animation: ${spinAnim} 0.8s linear infinite;
  }

  &:hover .arrow-icon {
    color: #6366f1;
    transform: translateX(3px);
  }
`;

const CompanyItemLogo = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 10px;
  color: ${semanticColors.text.secondary};
  overflow: hidden;
`;

const CompanyLogoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const CompanyItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const CompanyItemName = styled.span`
  ${typography.paragraph.p2.default}
  font-weight: 600;
  color: ${semanticColors.text.primary};
`;

const CompanyItemCode = styled.span`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
`;

const SelectFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px 32px;
  border-top: 1px solid ${semanticColors.border.primary};
  background: ${semanticColors.background.tertiary};
  ${typography.caption.caption2}
  color: ${semanticColors.text.disabled};
`;

// ─── Close / Result Card ─────────────────────────────────────────────────────

const CloseCard = styled.div`
  width: 100%;
  max-width: 400px;
  background: ${semanticColors.background.secondary};
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(0,0,0,0.1);
  border: 1px solid ${semanticColors.border.primary};
  animation: ${fadeInUp} 0.45s cubic-bezier(0.16,1,0.3,1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 48px 40px 0;
`;

const CloseIconWrapper = styled.div`
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 20px;
  color: white;
  animation: ${scaleIn} 0.5s cubic-bezier(0.34,1.56,0.64,1);
  box-shadow: 0 8px 20px rgba(16,185,129,0.25);
`;

const CloseHeading = styled.h2`
  ${typography.heading.h3}
  color: ${semanticColors.text.primary};
  margin: 16px 0 0;
`;

const CloseDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin: 10px 0 0;
  line-height: 1.7;
`;

const CloseButton = styled.button`
  margin-top: 24px;
  padding: 12px 32px;
  background: ${semanticColors.background.tertiary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  ${typography.paragraph.p2.default}
  font-weight: 600;
  color: ${semanticColors.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: ${semanticColors.background.secondary};
    border-color: ${semanticColors.border.secondary};
  }
`;

const CloseFooter = styled.div`
  width: calc(100% + 80px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px 40px;
  margin-top: 32px;
  border-top: 1px solid ${semanticColors.border.primary};
  background: ${semanticColors.background.tertiary};
  ${typography.caption.caption2}
  color: ${semanticColors.text.disabled};
`;

export default McpLogin;
